import { Cancel } from "@mui/icons-material";
import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import useDebounce from "src/hooks/useDebounce";
import useGetDataFromSelect from "src/hooks/useGetDataFromSelect";

export default function SearchList({ optionList = [], params: apiParams, initialShow, expect, ...props } = {}) {
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [selectedValue, setSelectedValue] = useState(props?.value ?? props?.defaultValue ?? null);

  const debouncedProductSearch = useDebounce(search, 400);
  const loadingNextPageRef = useRef(false);
  const initialShowApplied = useRef(false);

  const { data, isLoading, isValidating, size, setSize, hasMore } = useGetDataFromSelect(
    apiParams?.path,
    {
      ...apiParams?.params,
      searchValue: debouncedProductSearch ? debouncedProductSearch : props?.defaultSearch,
      limit: 20,
      sortOrder: "asc",
    },
    true,
  );

  const apiOptions = data?.data?.filter((v) => v?._id !== expect?._id) ?? [];
  const options = apiOptions.length > 0 ? apiOptions : optionList;

  useEffect(() => {
    if (props?.value !== undefined) {
      setSelectedValue(props.value);
    }
  }, [props?.value]);

  useEffect(() => {
    if (selectedValue) {
      setInputValue(typeof selectedValue === "string" ? selectedValue : (selectedValue?.name ?? selectedValue?.fullName ?? ""));
    } else {
      setInputValue("");
    }
  }, [selectedValue]);

  useEffect(() => {
    if (!isValidating) {
      loadingNextPageRef.current = false;
    }
  }, [isValidating]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(inputValue);
    }, 400);

    return () => {
      clearTimeout(timer);
    };
  }, [inputValue]);

  useEffect(() => {
    if (!initialShow || initialShowApplied.current) {
      return;
    }

    if (!selectedValue && options.length > 0) {
      initialShowApplied.current = true;

      setSelectedValue(options[0]);
      props?.onChange?.(null, options[0]);
    }
  }, [options, selectedValue, initialShow]);

  useEffect(() => {
    const hasValue = (value) => value != null && value !== "";

    if (hasValue(props?.value) || hasValue(props?.defaultValue)) {
      initialShowApplied.current = true;
    }
  }, [props?.value, props?.defaultValue]);

  const handleScroll = (event) => {
    const listboxNode = event.currentTarget;
    const isBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

    if (!isBottom) {
      return;
    }

    if (isLoading || isValidating || loadingNextPageRef.current || !hasMore) {
      return;
    }

    loadingNextPageRef.current = true;
    setSize(size + 1);
  };

  const handleChange = (_, newValue) => {
    setSelectedValue(newValue);

    if (props?.onChange) {
      props.onChange(_, newValue);
    }
  };

  return (
    <Autocomplete
      {...props}
      value={selectedValue}
      defaultValue={undefined}
      freeSolo
      options={options}
      loading={isLoading || isValidating}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);

        // When user clears the field, don't reapply initialShow.
        if (reason === "clear") {
          initialShowApplied.current = true;
          setSelectedValue(null);
        }
      }}
      clearIcon={<Cancel fontSize="small" />}
      getOptionLabel={props?.getOptionLabel ?? ((option) => (typeof option === "string" ? option : (option?.name ?? option?.fullName ?? "")))}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) {
          return option === value;
        }

        if (typeof option === "string" || typeof value === "string") {
          return option === value;
        }

        return option._id === value._id;
      }}
      ListboxProps={{
        onScroll: handleScroll,
      }}
      renderInput={(inputParams) => (
        <TextField
          {...inputParams}
          label={props?.label ?? "Tìm kiếm"}
          placeholder="Tìm kiếm..."
        />
      )}
    />
  );
}
